import { Api } from 'src/apis/api';
import { Gtm } from 'src/apis/gtm';
import { LeadController } from 'src/controllers/business/LeadController';
import { ForageService } from 'src/services/ForageService';
import { IFunnelProps } from 'src/types/funnel';
import { IKlaviyoEvent } from 'src/types/klaviyo';
import { IProduct } from 'src/types/product';
import { getCookie } from 'src/utils/cookies';
import {
  isOneClickCheckoutPage,
  isOneClickPage,
  navigate,
} from 'src/utils/navigation';
import { getSession } from 'src/utils/session';
import { capitalize } from 'src/utils/string';
import { getAppUtms } from 'src/utils/utms';

import {
  appendUrlParams,
  getUrlLocale,
  getUrlParameters,
  removeUrlParams,
} from '../../utils/url';
import {
  CheckoutFormController,
  IBuyButtonIds,
} from '../forms/CheckoutFormController';
import { CheckoutController } from '../pages/CheckoutController';
import { OneClickController } from '../pages/OneClickController';
import { ThankYouController } from '../pages/ThankyouController';
import { UpsellController } from '../pages/UpsellController';
import { UiController } from '../ui/UiController';

export class FunnelController {
  lead: LeadController;
  config: IFunnelProps;
  private products = pageProducts;
  private subscriptions = pageSubscriptions;
  private addons = window.pageAddons ? window.pageAddons : [];

  static reportNoAuidError = (config: IFunnelProps) => {
    // COMMENTED OUT 2025.07.07
    // const params = new URLSearchParams(window.location.search);
    // const auid = params.get('utm_auid');
    // const isTest = params.get('utm_source') === 'test';
    // if (!window.DEBUG && !auid && !isTest) {
    //   Errors.report(
    //     new Error('ProxyError: No auid found on production website'),
    //     {
    //       geo: window.GEO,
    //       config,
    //     },
    //     false,
    //     false,
    //   );
    // }
  };

  static init = async (config: IFunnelProps) => {
    try {
      const lead = await LeadController.init(config);
      return new FunnelController(config, lead);
    } catch (e) {
      Errors.report(
        e,
        {
          config,
        },
        false,
        false,
      );
    }
  };

  private constructor(config: IFunnelProps, lead: LeadController) {
    this.lead = lead;
    this.config = config;
    this.maybeInitOneClickProduct();
    this.reportUrlErrors();
    this.checkIfFreeShipping();
    UiController.removeInvisibleElements();

    // Change product upsellKey when upsell flow is custom
    if (typeof config.upsellKey === 'number') {
      const firstProductUpsellKey = this.products[0].upsellKey;
      if (
        typeof firstProductUpsellKey === 'number' &&
        firstProductUpsellKey !== config.upsellKey
      ) {
        this.products = this.products.map(p => ({
          ...p,
          upsellKey: config.upsellKey,
        }));
      }
    }

    if (window.DEBUG) {
      console.log('FunnelController:', this);
    }
  }

  private reportUrlErrors = () => {
    const params = getUrlParameters();
    removeUrlParams(['errorMsg']);
    if (typeof params['errorMsg'] === 'string') {
      Errors.report(
        new Error(params['errorMsg']),
        {
          geo: window.GEO,
          config: this.config,
          lead: this.lead?.lead,
          pp_profile: window.FLAGS.pp_profile,
        },
        false,
        false,
      );
    }
  };

  private initSession = async () => {
    const params = new URLSearchParams(window.location.search);
    const isConfirmPaypal = params.get('paypalAccept');
    if (!isConfirmPaypal) {
      try {
        const oldSessionId = isOneClickPage()
          ? undefined
          : params.get('session_id') || this.lead.lead?.sessionId;
        this.config.sessionId = await getSession({
          ip: window.GEO.ip,
          client: window.BRAND,
          sessionId: oldSessionId,
          pageType: this.config.pageType,
          campaignId: this.config.campaignId,
          leadEmail: this.lead.lead?.emailAddress,
          isCheckout: this.config.pageType === 'checkoutPage',
        });
      } catch (e) {
        Errors.report(e, {
          geo: window.GEO,
          config: this.config,
          lead: this.lead?.lead,
        });
        this.config.sessionId = '';
      }
    }
  };

  private reportAffId = () => {
    const params = getUrlParameters();
    if ('affid' in params) {
      Api.reportAffId({
        url: window.location.href,
        brand: window.BRAND,
        email: this.lead.lead?.emailAddress,
        orderId: this.lead.lead?.orderId || params?.['order_id'],
        sessionId: this.lead.lead?.sessionId || params?.['session_id'],
      });
    }
  };

  private checkIfFreeShipping = () => {
    const params = new URLSearchParams(window.location.search);
    const freeShippingUtm = params.get('utm_mail_ab');
    if (freeShippingUtm === '3fee3d5') {
      UiController.setFreeShipping();
      this.config.shippingPrice = 0;
      this.products.forEach(p => {
        p.shippingPrice = 0;
      });
    }
  };

  private maybeInitOneClickProduct = async () => {
    if (isOneClickCheckoutPage()) {
      const storage = ForageService.getInstance();
      const pid =
        getUrlParameters('pid') ||
        (await storage.getValue<string>('express_product_id'));
      const selectedIdx = this.products.findIndex(p => p.id.toString() === pid);
      const pidx = selectedIdx !== -1 ? selectedIdx : 0;
      window['productIdx'].value = pidx;
      UiController.populateOneClickProductCard(this.products[pidx]);
    }
  };

  initSubscriptionPaymentBasedRouting = (payload: {
    oneTimePaymentSelecorId: string;
    oneTimePaymentRoutingPath: string;
    subscriptionPaymentSelecorId: string;
    subscriptionPaymentRoutingPath: string;
  }) => {
    try {
      if (
        payload.oneTimePaymentSelecorId &&
        payload.oneTimePaymentRoutingPath &&
        payload.subscriptionPaymentSelecorId &&
        payload.subscriptionPaymentRoutingPath
      ) {
        const selectedCookieProduct: any = this.getProductFromCookie();
        const subscriptionSelected = selectedCookieProduct
          ? JSON.parse(selectedCookieProduct)?.subscription
          : undefined;
        if (window.DEBUG) {
          console.log(`selected product - `, selectedCookieProduct);
        }
        if (subscriptionSelected || subscriptionSelected === undefined) {
          this.config.redirectTo = payload.subscriptionPaymentRoutingPath;
          if (window.DEBUG) {
            console.log(
              `setting default redirect to - ${payload.subscriptionPaymentRoutingPath}`,
            );
          }
        }

        document
          .querySelectorAll(`#${payload.subscriptionPaymentSelecorId}`)
          .forEach(b => {
            b.addEventListener('click', () => {
              this.config.redirectTo = payload.subscriptionPaymentRoutingPath;
              if (window.DEBUG) {
                console.log(
                  `setting redirect to - ${payload.subscriptionPaymentRoutingPath}`,
                );
              }
            });
          });
        document
          .querySelectorAll(`#${payload.oneTimePaymentSelecorId}`)
          .forEach(b => {
            b.addEventListener('click', () => {
              this.config.redirectTo = payload.oneTimePaymentRoutingPath;
              if (window.DEBUG) {
                console.log(
                  `setting redirect to - ${payload.oneTimePaymentRoutingPath}`,
                );
              }
            });
          });
      }
    } catch (e) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.config,
        lead: this.lead?.lead,
      });
      this.config.sessionId = '';
    }
  };

  private confirmPayPalOrder = async (
    controller: CheckoutController,
    redirectTo: string,
  ) => {
    const params = getUrlParameters();
    const ppToken = params['token'];
    const baToken = params['ba_token'];
    const confirmed = params['paypalAccept']?.toLowerCase() === '1';
    const payerId = params['PayerID'];

    removeUrlParams(['token', 'ba_token', 'paypalAccept']);

    const storage = ForageService.getInstance();
    const [paypalMid, sessionId, productId] = await Promise.all([
      storage.getValue<string>('express_paypal_mid'),
      storage.getValue<string>('express_session_id'),
      storage.getValue<string>('express_product_id'),
    ]);

    const referrer = window.document.referrer;
    const hasAllParams = paypalMid && sessionId && productId;
    if (window.DEBUG) {
      console.log('confirmPayPalOrder:', {
        ppToken,
        baToken,
        confirmed,
        paypalMid,
        sessionId,
        productId,
        params,
        referrer,
      });
    }

    if (referrer.includes('paypal.com') && confirmed && !hasAllParams) {
      const message = `PayPalError: Express missing params: paypalMid: ${paypalMid}, sessionId: ${sessionId}, productId: ${productId}`;
      Errors.report(
        new Error(message),
        {
          paypalMid,
          sessionId,
          productId,
          referrer,
        },
        false,
        false,
      );
      return;
    }

    const findProductOrSubscription = (id: string | null) => {
      if (!id) return null;
      const product = this.products.find(p => p.id.toString() === id);
      if (product) {
        return product;
      }

      if (this.subscriptions && this.subscriptions.length > 0) {
        const subscriptionProduct = this.subscriptions.find(
          p => p.id.toString() === id,
        );
        if (subscriptionProduct) {
          return subscriptionProduct;
        }
      }
    };

    const productOrSubscription: any = findProductOrSubscription(productId);
    await Promise.all([
      storage.deleteValue('express_paypal_mid'),
      storage.deleteValue('express_session_id'),
      storage.deleteValue('express_product_id'),
    ]);

    if (confirmed && paypalMid && ppToken && baToken && sessionId) {
      this.lead.lead.sessionId = sessionId;
      await controller.confirmPaypalOrder({
        baToken,
        ppToken,
        sessionId,
        redirectTo,
        paypalMid,
        product: productOrSubscription,
      });
    }

    //Logic for paypal without Payment reference.
    if (confirmed && paypalMid && ppToken && sessionId && !baToken && payerId) {
      this.lead.lead.sessionId = sessionId;
      await controller.confirmPaypalOrderWOBaToken({
        payerId,
        ppToken: ppToken ?? '',
        sessionId: sessionId ?? '',
        redirectTo: redirectTo ?? '',
        paypalMid: paypalMid ?? '',
        product: productOrSubscription ?? undefined,
      });
    }
  };

  getCurrency = () => {
    const product = this.products[0];
    if (product) {
      return product.currencyCode;
    }
    return undefined;
  };

  getProductFromCookie = () => {
    const selectedProduct = getCookie('selected');
    return selectedProduct;
  };

  getSelectedProduct = (productId?: string | null) => {
    let product;
    if (productId) {
      // Search by ID in both subscriptions and products
      product =
        this.subscriptions.find(p => p.id.toString() === productId) ||
        this.products.find(p => p.id.toString() === productId);
    } else {
      // Get product by index from either subscriptions or products array
      const productIdx = parseInt(window['productIdx']?.value, 10);
      product = window.subSelected
        ? this.subscriptions[productIdx]
        : this.products[productIdx];
    }

    // Save product size if it exists for socks 1click
    if (product?.size) {
      localStorage.setItem('product_size', product.size);
    }

    return product;
  };

  getSelectedAddon = () => {
    const addonIdxStr = window['addonIdx']?.value;
    const addonIdx = parseInt(addonIdxStr, 10);
    const addon = this.addons[addonIdx];

    if (window.DEBUG) {
      console.log(addon);
    }

    return addon;
  };

  getSelectedAddons = () => {
    const addonIdxs: any[] = window['addonIdxs'] ?? [];
    if (window.pageAddons && Array.isArray(window.pageAddons)) {
      const addons = window.pageAddons
        .map((el: IProduct, index: number) => {
          const addonIdx = addonIdxs.find(addon => addon?.value === index);
          if (addonIdx && addonIdx.quantity) {
            return { ...el, quantity: addonIdx.quantity };
          }
          return null;
        })
        .filter(Boolean);

      if (window.DEBUG) {
        console.log('Selected Addons:', addons);
      }

      return addons;
    }
  };

  addAddonsToPurchasePayload = (payload: any) => {
    const addons = this.getSelectedAddons();
    if (addons && addons.length > 0) {
      let note = payload?.note;
      
      addons.forEach((addon: IProduct, index: number) => {
        if (window.DEBUG) {
          console.log(addon);
        }
        
        note = `${note}, addon_${index + 1}`;

        const addonDiscountPrice: number = addon.discountPrice;
        const addonFinalPrice: number = addon.finalPrice;
        payload[`product${index + 2}_id`] = String(addon?.id);
        payload[`product${index + 2}_price`] = addonDiscountPrice
          ? addonFinalPrice - addonDiscountPrice
          : addonFinalPrice;
        payload[`product${index + 2}_shipPrice`] = addon?.shippingPrice || 0;
        payload[`product${index + 2}_qty`] = 1;
      });

      payload.note = note;
    }
  };

  listenForEvents = () => {
    window.addEventListener('klaviyoForms', e => {
      const ev = e as unknown as CustomEvent<IKlaviyoEvent>;
      if (ev.detail.type === 'open') {
        Gtm.trackEmailPopupOpen();
      }
      if (ev.detail.type === 'close') {
        Gtm.trackEmailPopupClose();
      }
      if (ev.detail.type === 'submit') {
        appendUrlParams({
          utm_medium: 'popup',
        });
        Gtm.trackPopupEmailSubmit(ev.detail.metaData.$email);
        setTimeout(() => {
          const closeBtn = document.querySelector('.klaviyo-close-form');
          if (closeBtn) {
            (closeBtn as HTMLButtonElement)?.click();
          }
        }, 3000);
        const crossedPrices: NodeListOf<HTMLDivElement> =
          document.querySelectorAll('#crossed-price');
        crossedPrices.forEach(el => {
          el.style.display = 'flex';
        });
        this.products.forEach(p => {
          const reducedPrice = p.popupPrice
            ? parseFloat(p.popupPrice)
            : p.finalPrice;
          p.finalPrice = reducedPrice;
          const productCard = document.querySelector(
            `[data-product-id="${p.id}"]`,
          );
          if (productCard) {
            const finalPrice = productCard.querySelector('#finalPrice');
            if (finalPrice) {
              finalPrice.textContent = reducedPrice.toString();
            }
          }
        });
      }
    });
  };

  initCheckout = async (props: IBuyButtonIds) => {
    try {
      setTimeout(() => FunnelController.reportNoAuidError(this.config), 500);
      const controller = new CheckoutController(this);
      controller.maybeInitKlarna(this.lead?.lead?.country);
      controller.maybeInitBraintree(props);
      controller.maybeInitStripeApplePay();

      const checkoutForm = new CheckoutFormController(this, controller, props);
      checkoutForm.initCheckoutForm();
      await this.initSession();

      await this.confirmPayPalOrder(controller, this.config.redirectTo || '');

      if (this.config?.trackingName) {
        Gtm.trackPageView('Order', this.config.trackingName);
      }
    } catch (e) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.config,
        lead: this.lead.lead,
      });
    }
  };

  initUpsell = async (upsellKey: number) => {
    try {
      appendUrlParams({
        promo_step: (upsellKey + 1).toString(),
      });
      await this.initSession();
      this.reportAffId();
      const controller = new UpsellController(this, upsellKey);

      // Inject lead name 1st upsell
      if (this.lead?.lead?.firstName?.length > 0) {
        const firstName = this.lead?.lead?.firstName.trim();
        UiController.injectLeadName('leadNameU0', `${capitalize(firstName)}, `);
      }

      document.querySelectorAll('#buyButton').forEach(btn => {
        btn.addEventListener('click', e => {
          e.preventDefault();
          controller.createUpsellOrder(upsellKey);
        });
      });
      if (this.config?.trackingName) {
        Gtm.trackPageView(`Upsell${upsellKey + 1}`, this.config.trackingName);
      }

      await controller.confirmPayPalOrder(this.config.redirectTo || '');
    } catch (e) {
      Errors.report(e, {
        upsellKey,
        geo: window.GEO,
        config: this.config,
        lead: this.lead.lead,
      });
    }
  };

  private shouldReportInvalidOneClick = () => {
    const params = getUrlParameters();
    const orderId = params['order_id'];
    const prospectId = params['prospect_id'];
    const notTest = params['utm_source'] !== 'test';
    return notTest && !orderId && !prospectId;
  };

  initOneClick = async () => {
    try {
      setTimeout(() => FunnelController.reportNoAuidError(this.config), 500);
      await this.initSession();
      const controller = new OneClickController(this);
      // Inject lead name in one click
      if (this.lead?.lead?.firstName?.length > 0) {
        const firstName = this.lead?.lead?.firstName.trim();
        UiController.injectLeadName('leadNameU0', `${capitalize(firstName)}, `);
      }

      if (this.config?.trackingName) {
        Gtm.trackPageView(this.config.pageType, this.config.trackingName);
      }

      if (this.shouldReportInvalidOneClick()) {
        Errors.report(
          new Error('OneClickError: Missing order_id and prospect_id'),
          {
            geo: window.GEO,
            config: this.config,
            lead: this.lead.lead,
          },
          false,
          false,
        );
      }

      document.querySelectorAll('#buyButton').forEach(btn => {
        btn.addEventListener('click', async ev => {
          ev.preventDefault();

          const auid = getAppUtms()?.custom_order_auid;
          const sessionId =
            new URLSearchParams(window.location.search).get('session_id') || '';

          Gtm.trackBuyButtonClick(
            this.lead.lead?.customerId?.toString() || '',
            this.lead.lead?.orderId || '',
            'one_click',
            auid,
            sessionId,
          );
          const status = await controller.createOneClickOrder();
          if (status === 'block') {
            UiController.showErrorModal(
              'You have recently made a purchase, please check back later as we limit special discount offers.',
            );
          }
          if (status === 'error') {
            const locale = getUrlLocale();
            const checkoutSlug = this.config?.oneClickCheckoutSlug
              ? this.config.oneClickCheckoutSlug
              : locale;
            const product = this.getSelectedProduct();
            const checkoutUrl = `/products-order/${checkoutSlug}`;
            if (window.DEBUG) {
              console.log('TO_CHECKOUT 1:', checkoutUrl);
            }
            navigate(checkoutUrl, this.lead?.lead, {
              pid: product.id.toString(),
            });
          }
        });
      });
    } catch (e) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.config,
        lead: this.lead.lead,
      });
    }
  };

  initThankYouPage = async () => {
    try {
      await this.initSession();
      this.reportAffId();
      setTimeout(() => {
        const controller = new ThankYouController(this);
        controller.injectInfo();
      }, 500);
    } catch (e) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.config,
        lead: this.lead.lead,
      });
    }
  };

  replaceTextInHtml = (id: string, replaceStr: string, replaceWith: string) => {
    const element = document.querySelector<HTMLElement>(id);
    if (element) {
      element.innerHTML = element.innerHTML.replace(replaceStr, replaceWith);
    }
  };

  initPersonalizedHeader = (
    skeletonId: string,
    defaultHeaderId: string,
    personalizedHeaderId: string,
    replaceStr = '{name}',
  ) => {
    const skeleton = document.querySelector(skeletonId);
    const defaultHeader = document.querySelector<HTMLElement>(defaultHeaderId);
    const personalizedHeader =
      document.querySelector<HTMLElement>(personalizedHeaderId);
    skeleton?.remove();

    if (skeleton && (!personalizedHeader || !defaultHeader)) {
      Errors.report(
        new Error(`UiError: error with personalized heading`),
        undefined,
        false,
        false,
      );
      return;
    }

    if (this.lead?.lead?.firstName?.length > 0) {
      const firstName = capitalize(this.lead.lead.firstName.trim());
      if (personalizedHeader) {
        const replacedHtml = personalizedHeader.innerHTML.replace(
          replaceStr,
          firstName,
        );
        personalizedHeader.innerHTML = replacedHtml;
        personalizedHeader.style.display = 'block';
      }
    } else if (defaultHeader) {
      defaultHeader.style.display = 'block';
    }
  };

  // --------------------------------------- BELOW IS AOV TEST CODE BLOCK TO REMOVE IF IT FAILS ---------------------------------------
  // Padejau cia kad nestovetu front'e, po testo istrinsiu arba sutvarkysiu
  initAddonAovTest = () => {
    function getDiscountPrice(baseTotal: number) {
      const discountPercentage = baseTotal >= 80 ? 0.1 : 0;
      let discountPrice = 0;
      const productIdx = window['productIdx'];
      if (productIdx) {
        const selectedProduct =
          window.pageProducts[parseInt(productIdx.value, 10)];
        if (selectedProduct) {
          discountPrice += parseFloat(
            (selectedProduct.finalPrice * discountPercentage).toFixed(2),
          );
        }
      }
      const selectedAddonIdxs = window['addonIdxs'];
      if (selectedAddonIdxs) {
        selectedAddonIdxs.forEach(function (addonIdx: {
          value: string;
          quantity: string;
        }) {
          const idx = parseInt(addonIdx.value, 10);
          const selectedAddon = window.pageAddons[idx];
          if (selectedAddon) {
            discountPrice += parseFloat(
              (selectedAddon.finalPrice * discountPercentage).toFixed(2),
            );
          }
        });
      }
      return discountPrice;
    }
    // Helper functions: Get base prices (without shipping)
    function getSelectedProductBasePrice() {
      let price = 0;
      const productIdx = window['productIdx'];
      if (productIdx) {
        const selectedProduct =
          window.pageProducts[parseInt(productIdx.value, 10)];
        if (selectedProduct) {
          price += selectedProduct.finalPrice;
        }
      }
      return price;
    }

    function getSelectedAddonBasePrice() {
      let price = 0;
      const selectedAddonIdxs = window['addonIdxs'];
      if (selectedAddonIdxs) {
        selectedAddonIdxs.forEach(function (addonIdx: {
          value: string;
          quantity: string;
        }) {
          const idx = parseInt(addonIdx.value, 10);
          const pageAddons = window.pageAddons;
          if (pageAddons && Array.isArray(pageAddons)) {
            const selectedAddon = pageAddons[idx];
            if (selectedAddon) {
              price += selectedAddon.finalPrice;
            }
          }
        });
      }
      return price;
    }

    // Update shipping prices based on base total
    function updateShippingPrices(baseAmount: number) {
      // Shipping is free if baseAmount >= 60, otherwise 4.95
      const shippingPrice = baseAmount >= 60 ? 0 : 4.95;

      // Update shipping price for each product
      window.pageProducts.forEach(function (product: IProduct) {
        product.shippingPrice = shippingPrice;
      });

      // Update the shipping price in DOM elements (if used)
      document.querySelectorAll('#productItem').forEach(function (item) {
        item.setAttribute('data-shipping-price', shippingPrice.toString());
      });

      const standartPriceBoxes = document.querySelectorAll(
        '#standart-price-box',
      );
      const freePriceBoxes = document.querySelectorAll('#free-price-box');
      if (baseAmount >= 60) {
        standartPriceBoxes.forEach((standartPriceBox: any) => {
          if (standartPriceBox) {
            standartPriceBox.style.display = 'none';
          }
        });
        freePriceBoxes.forEach((freePriceBox: any) => {
          if (freePriceBox) {
            freePriceBox.style.display = 'flex';
          }
        });
      } else {
        standartPriceBoxes.forEach((standartPriceBox: any) => {
          if (standartPriceBox) {
            standartPriceBox.style.display = 'flex';
          }
        });
        freePriceBoxes.forEach((freePriceBox: any) => {
          if (freePriceBox) {
            freePriceBox.style.display = 'none';
          }
        });
      }

      return shippingPrice;
    }

    // Update visual progress points (and addonIdxs) – works with SVGs having multiple path elements
    function updateProgressPoints(amount: number) {
      // Define thresholds and corresponding background/SVG container elements
      const thresholds = [60, 80, 100];
      const bgElements = [
        document.getElementById('addon-progress-poin1-bg'),
        document.getElementById('addon-progress-poin2-bg'),
        document.getElementById('addon-progress-poin3-bg'),
      ];
      const svgContainers = [
        document.getElementById('addon-progress-poin1-img'),
        document.getElementById('addon-progress-poin2-img'),
        document.getElementById('addon-progress-poin3-img'),
      ];

      thresholds.forEach((threshold, index) => {
        if (amount >= threshold) {
          if (bgElements[index]) {
            bgElements[index].style.backgroundImage =
              'linear-gradient(135deg, #c322eb, #381743)';
          }
          if (svgContainers[index]) {
            // Update fill for every path element inside the SVG container
            const paths = svgContainers[index].querySelectorAll('path');
            paths.forEach(path => {
              path.style.fill = '#fff';
            });
          }
        } else {
          if (bgElements[index]) {
            bgElements[index].style.backgroundImage = 'none';
          }
          if (svgContainers[index]) {
            // Revert fill for every path element inside the SVG container
            const paths = svgContainers[index].querySelectorAll('path');
            paths.forEach(path => {
              path.style.fill = '#BF48E0';
            });
          }
        }
      });

      updateShippingPrices(amount);

      if (
        amount >= 100 &&
        window.addonIdxs &&
        Array.isArray(window.addonIdxs)
      ) {
        const found = window.addonIdxs.find(obj => obj.value === 8);
        if (!found) {
          window.addonIdxs.push({ value: 8, quantity: 1 });
        }
      } else if (
        amount < 100 &&
        window.addonIdxs &&
        Array.isArray(window.addonIdxs)
      ) {
        window.addonIdxs = window.addonIdxs.filter(obj => obj.value !== 8);
      }
    }

    // Update progress bar width based on total amount
    function updateProgressBar(totalAmount: number) {
      const progressBarElement: any = document.querySelector(
        '#addon-progress-bar',
      );
      let progress;
      if (totalAmount < 60) {
        progress = totalAmount * 0.55;
      } else {
        progress = 33 + (totalAmount - 60) * 1.675;
      }
      progress = Math.min(progress, 100);
      if (progressBarElement) {
        progressBarElement.style.width = progress.toString() + '%';
      }
      updateProgressPoints(totalAmount);
    }

    const setTotalAmount = () => {
      const baseTotal =
        getSelectedProductBasePrice() + getSelectedAddonBasePrice();
      if (window.DEBUG) {
        console.log('baseTotal: ', baseTotal);
      }
      const subtotalAmount = parseFloat(baseTotal.toFixed(2));
      if (window.DEBUG) {
        console.log('subtotalAmount: ', subtotalAmount);
      }
      const subtotalAmountText: any = document.querySelector(
        '#addon-progress-subtotal-price',
      );
      if (subtotalAmountText) {
        subtotalAmountText.innerText = subtotalAmount;
      }

      const discountAmount = getDiscountPrice(baseTotal)?.toFixed(2);
      if (window.DEBUG) {
        console.log('discountAmount: ', discountAmount);
      }
      const discountElement: any = document.getElementById(
        'addon-progress-discount-price',
      );
      if (discountElement) {
        discountElement.innerText = discountAmount;
      }

      if (baseTotal >= 80) {
        if (
          Array.isArray(window.pageProducts) &&
          Array.isArray(this.products)
        ) {
          window.pageProducts.forEach(el => {
            el.discountPrice = parseFloat((el.finalPrice * 0.1).toFixed(2));
          });
          this.products.forEach(el => {
            el.discountPrice = parseFloat((el.finalPrice * 0.1).toFixed(2));
          });
        }
        if (Array.isArray(window.pageAddons) && Array.isArray(this.addons)) {
          window.pageAddons.forEach(el => {
            el.discountPrice = parseFloat((el.finalPrice * 0.1).toFixed(2));
          });
          this.addons.forEach(el => {
            el.discountPrice = parseFloat((el.finalPrice * 0.1).toFixed(2));
          });
        }
      } else {
        if (
          Array.isArray(window.pageProducts) &&
          Array.isArray(this.products)
        ) {
          window.pageProducts.forEach(el => {
            el.discountPrice = 0;
          });
          this.products.forEach(el => {
            el.discountPrice = 0;
          });
        }
        if (Array.isArray(window.pageAddons) && Array.isArray(this.addons)) {
          window.pageAddons.forEach(el => {
            el.discountPrice = 0;
          });
          this.addons.forEach(el => {
            el.discountPrice = 0;
          });
        }
      }

      const shippingPrice = updateShippingPrices(baseTotal);
      if (window.DEBUG) {
        console.log('shippingPrice: ', shippingPrice);
      }
      const totalAmount = parseFloat(
        (baseTotal - discountAmount + shippingPrice).toFixed(2),
      );
      if (window.DEBUG) {
        console.log('totalAmount: ', totalAmount);
      }
      const totalElement: any = document.getElementById(
        'addon-progress-total-price',
      );
      if (totalElement) {
        totalElement.innerText = totalAmount;
      }

      updateProgressBar(baseTotal);
    };

    window.setTotalAmount = setTotalAmount;

    function initAddonButtons() {
      const cookieAddonIdxs = getCookie('addonIdxs');
      if (cookieAddonIdxs) {
        window['addonIdxs'] = JSON.parse(cookieAddonIdxs);
      }

      // Retrieve the allowed range for each product type from the corresponding add button.
      const nasalAddButton = document.getElementById('nasal-addon-add-button');
      const sleepsprayAddButton = document.getElementById(
        'sleepspray-addon-add-button',
      );

      let nasalMin = 0,
        nasalMax = 0,
        sleepsprayMin = 0,
        sleepsprayMax = 0;
      if (nasalAddButton) {
        nasalMin = parseInt(
          nasalAddButton.getAttribute('data-addon-min-idx') || '0',
          10,
        );
        nasalMax = parseInt(
          nasalAddButton.getAttribute('data-addon-max-idx') || '0',
          10,
        );
      }
      if (sleepsprayAddButton) {
        sleepsprayMin = parseInt(
          sleepsprayAddButton.getAttribute('data-addon-min-idx') || '0',
          10,
        );
        sleepsprayMax = parseInt(
          sleepsprayAddButton.getAttribute('data-addon-max-idx') || '0',
          10,
        );
      }

      // Calculate total quantities for each product type.
      let nasalQuantity = 0;
      let sleepsprayQuantity = 0;

      if (Array.isArray(window['addonIdxs'])) {
        window['addonIdxs'].forEach(item => {
          if (item && item.quantity != null) {
            const qty = parseInt(item.quantity, 10) || 0;
            // Only count an item if its value falls within the corresponding range.
            if (item.value >= nasalMin && item.value <= nasalMax) {
              nasalQuantity += qty;
            } else if (
              item.value >= sleepsprayMin &&
              item.value <= sleepsprayMax
            ) {
              sleepsprayQuantity += qty;
            }
          }
        });
      }

      // Update quantity display elements.
      const nasalQntElem = document.getElementById('nasal-addon-quantity');
      const sleepsprayQntElem = document.getElementById(
        'sleepspray-addon-quantity',
      );
      if (nasalQntElem) {
        nasalQntElem.innerText = nasalQuantity.toString();
      }
      if (sleepsprayQntElem) {
        sleepsprayQntElem.innerText = sleepsprayQuantity.toString();
      }

      // Update button statuses for nasal add-ons.
      const nasalAddBtn: any = document.getElementById(
        'nasal-addon-add-button',
      );
      const nasalRemoveBtn: any = document.getElementById(
        'nasal-addon-remove-button',
      );
      if (nasalAddBtn) {
        if (nasalQuantity >= 4) {
          nasalAddBtn.disabled = true;
          nasalAddBtn.style.opacity = '0.5';
        } else {
          nasalAddBtn.disabled = false;
          nasalAddBtn.style.opacity = '1';
        }
      }
      if (nasalRemoveBtn) {
        if (nasalQuantity > 0) {
          nasalRemoveBtn.disabled = false;
          nasalRemoveBtn.style.opacity = '1';
        } else {
          nasalRemoveBtn.disabled = true;
          nasalRemoveBtn.style.opacity = '0.5';
        }
      }

      // Update button statuses for sleepspray add-ons.
      const sleepsprayAddBtn: any = document.getElementById(
        'sleepspray-addon-add-button',
      );
      const sleepsprayRemoveBtn: any = document.getElementById(
        'sleepspray-addon-remove-button',
      );
      if (sleepsprayAddBtn) {
        if (sleepsprayQuantity >= 4) {
          sleepsprayAddBtn.disabled = true;
          sleepsprayAddBtn.style.opacity = '0.5';
        } else {
          sleepsprayAddBtn.disabled = false;
          sleepsprayAddBtn.style.opacity = '1';
        }
      }
      if (sleepsprayRemoveBtn) {
        if (sleepsprayQuantity > 0) {
          sleepsprayRemoveBtn.disabled = false;
          sleepsprayRemoveBtn.style.opacity = '1';
        } else {
          sleepsprayRemoveBtn.disabled = true;
          sleepsprayRemoveBtn.style.opacity = '0.5';
        }
      }

      // Optionally update styling/text of the add buttons.
      const nasalAddBtnElement: any =
        document.querySelector('.nasal-add-button');
      if (nasalAddBtnElement) {
        if (nasalQuantity === 0) {
          nasalAddBtnElement.style.position = 'absolute';
          nasalAddBtnElement.innerText = '+Add';
        } else {
          nasalAddBtnElement.style.position = 'relative';
          nasalAddBtnElement.innerText = '+';
        }
      }
      const sleepsprayAddBtnElement: any = document.querySelector(
        '.sleepspray-add-button',
      );
      if (sleepsprayAddBtnElement) {
        if (sleepsprayQuantity === 0) {
          sleepsprayAddBtnElement.style.position = 'absolute';
          sleepsprayAddBtnElement.innerText = '+Add';
        } else {
          sleepsprayAddBtnElement.style.position = 'relative';
          sleepsprayAddBtnElement.innerText = '+';
        }
      }
    }

    return {
      setTotalAmount,
      initAddonButtons,
    };
  };
}
